/**
 * Observability integration for the network client
 *
 * This module provides hooks to integrate with @workspace/observability.
 * The observability package is an optional peer dependency.
 *
 * When OpenTelemetry is initialized (via @workspace/observability/node),
 * HTTP auto-instrumentation automatically creates spans for all fetch calls.
 * The infraViz fire-and-forget posting is kept for backward compatibility
 * but can be omitted when OTel is active.
 */

import type { ClientConfig } from './types.js';

/**
 * Observability hooks that can be passed to client config
 */
export interface ObservabilityHooks {
  /** Called before each request */
  onRequest?: (url: string, method: string, metadata?: Record<string, unknown>) => void;
  /** Called after successful response */
  onResponse?: (url: string, method: string, status: number, durationMs: number) => void;
  /** Called on error */
  onError?: (url: string, method: string, error: string, durationMs: number) => void;
}

/**
 * Create client config with observability integration.
 *
 * If @workspace/observability is available and configured, this will
 * automatically trace all requests made by the client.
 *
 * @example
 * ```ts
 * import { createClient } from '@workspace/network';
 * import { withObservability } from '@workspace/network/observability';
 *
 * const client = createClient(withObservability({
 *   baseUrl: 'https://api.example.com',
 * }));
 * ```
 */
export function withObservability(
  config: ClientConfig,
  hooks?: ObservabilityHooks
): ClientConfig {
  const startTimes = new WeakMap<object, number>();

  return {
    ...config,
    onRequest: async (url, init) => {
      const startTime = Date.now();
      // Use the init object as a key to track timing
      startTimes.set(init, startTime);

      const method = (init.method ?? 'GET').toUpperCase();
      hooks?.onRequest?.(url, method);

      // Chain with existing onRequest if present
      if (config.onRequest) {
        return config.onRequest(url, init);
      }
      return init;
    },
    onResponse: async (response, data) => {
      // Note: we can't reliably recover the init object here to look up timing,
      // so we fall back to 0. Use createTracingFetch for accurate duration tracking.
      hooks?.onResponse?.(response.url, 'GET', response.status, 0);

      // Chain with existing onResponse if present
      if (config.onResponse) {
        return config.onResponse(response, data);
      }
      return data;
    },
    onError: async (error) => {
      hooks?.onError?.(error.url ?? 'unknown', error.method ?? 'GET', error.message, 0);

      // Chain with existing onError if present
      if (config.onError) {
        return config.onError(error);
      }
    },
  };
}

/**
 * @deprecated Use OTel auto-instrumentation instead. When @workspace/observability/node
 * is initialized, all HTTP requests are automatically traced without needing infraViz.
 * This interface is kept for backward compatibility.
 */
export interface InfraVizOptions {
  /** Service ID (must match the app's package.json name) */
  source: string;
  /**
   * Base URL for the telemetry API. Defaults to `TELEMETRY_API_URL`, then
   * `NEXT_PUBLIC_TELEMETRY_API_URL` (the gateway injects it into every child,
   * per stage). With none of the three set, no beacon is sent: there is no
   * stage-correct default to guess, and a guessed one (localhost, port 11139)
   * is how every stage's beacon used to land on LOCAL's telemetry-api.
   */
  url?: string;
  /** Optional callback that returns the current user ID (from request context) */
  getUserId?: () => string | undefined;
}

function extractPath(url: string): string {
  try { return new URL(url).pathname; }
  catch { return url; }
}

function extractTarget(url: string): string {
  try { return new URL(url).host; }
  catch { return url; }
}

/** Send a trace event to the telemetry service (fire-and-forget, never throws) */
function sendTrace(infraVizUrl: string, trace: Record<string, unknown>): void {
  // dl-0009 service token (scope telemetry:ingest); optional while
  // telemetry-api's service routes run in report mode. Server-side only:
  // the beacon never runs in a browser (see isNonNodeRuntime).
  const token = typeof process !== 'undefined' ? process.env?.TELEMETRY_SERVICE_TOKEN : undefined;
  globalThis.fetch(`${infraVizUrl}/api/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(trace),
    signal: AbortSignal.timeout(2000),
  }).catch(() => {});
}

/**
 * True in any JavaScript runtime that is not a Node server: a page, a web or
 * service worker, an edge runtime.
 *
 * The beacon is server-side only. Its endpoint (`POST /api/events` on
 * telemetry-api) is a service route needing a `telemetry:ingest` token, which a
 * browser can never hold, and browser fetches are already traced by
 * `@workspace/observability/browser` (FetchInstrumentation). The `window` test
 * is the one bundlers fold to a constant in a client build, so everything
 * behind it, endpoint resolution included, is dead code there; the
 * `process.versions.node` test covers workers, which have no `window`.
 */
export function isNonNodeRuntime(): boolean {
  if (typeof window !== 'undefined') return true;
  return typeof process === 'undefined' || !process.versions?.node;
}

/**
 * Where a server-side beacon posts, or `undefined` for "send nothing".
 *
 * No hardcoded fallback. `TELEMETRY_URL` (which no stage sets) and then
 * localhost port 11139 used to be the default, so a dev or prod server beacon
 * on this box posted to LOCAL's telemetry-api (and anywhere else, to nothing),
 * and the literal was compiled into every client bundle importing
 * `@workspace/ui`'s auth, where it could never run.
 */
export function resolveInfraVizUrl(
  infraViz: Pick<InfraVizOptions, 'url'> | undefined,
  env: Record<string, string | undefined> | undefined = typeof process !== 'undefined' ? process.env : undefined,
): string | undefined {
  if (!infraViz || isNonNodeRuntime()) return undefined;
  const url = infraViz.url || env?.TELEMETRY_API_URL || env?.NEXT_PUBLIC_TELEMETRY_API_URL;
  return url ? url.replace(/\/+$/, '') : undefined;
}

/** Skip the beacon when OTel is active server-side: HTTP instrumentation creates the spans. */
function otelActive(): boolean {
  try {
    const api = globalThis as Record<string, unknown>;
    return Object.getOwnPropertySymbols(api).some(
      s => s.toString().includes('opentelemetry')
    );
  } catch {
    return false;
  }
}

let traceCounter = 0;

/**
 * Create a traced fetch function that can be passed to the client.
 *
 * This wraps native fetch with timing and logging, suitable for use
 * with the `fetch` option in ClientConfig.
 *
 * When OTel is active (via @workspace/observability/node), the infraViz
 * fire-and-forget posting is automatically skipped since OTel HTTP
 * instrumentation handles span creation. Console logging hooks still fire.
 *
 * @example
 * ```ts
 * import { createClient, createTracingFetch } from '@workspace/network';
 *
 * const tracedFetch = createTracingFetch({
 *   onRequest: (url, method) => console.log(`→ ${method} ${url}`),
 *   onResponse: (url, method, status, ms) => console.log(`← ${status} (${ms}ms)`),
 *   infraViz: { source: 'my-service' },
 * });
 *
 * const client = createClient({
 *   baseUrl: 'https://api.example.com',
 *   fetch: tracedFetch,
 * });
 * ```
 */
export function createTracingFetch(options?: {
  onRequest?: (url: string, method: string) => void;
  onResponse?: (url: string, method: string, status: number, durationMs: number) => void;
  onError?: (url: string, method: string, error: string, durationMs: number) => void;
  /**
   * When set, a server-side process posts trace events to the telemetry API
   * for real-time visualization. Ignored in a browser.
   * @deprecated Automatically skipped when OTel is active. Will be removed in a future version.
   */
  infraViz?: InfraVizOptions;
}): typeof fetch {
  const infraViz = options?.infraViz;
  // Undefined in a browser (whatever the caller passed), and when no endpoint
  // is configured. See resolveInfraVizUrl.
  const infraVizUrl = resolveInfraVizUrl(infraViz);

  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const method = (init?.method ?? 'GET').toUpperCase();
    const startTime = Date.now();

    // Skip legacy infra-viz posting when OTel handles tracing. A browser never
    // gets this far: infraVizUrl is undefined there.
    const useInfraViz = infraViz && infraVizUrl && !otelActive();
    const traceId = useInfraViz ? `${infraViz.source}-${Date.now()}-${++traceCounter}` : '';
    const userId = useInfraViz ? infraViz.getUserId?.() : undefined;

    options?.onRequest?.(url, method);

    if (useInfraViz) {
      sendTrace(infraVizUrl, {
        id: traceId,
        type: 'request',
        timestamp: startTime,
        source: infraViz.source,
        target: extractTarget(url),
        method,
        path: extractPath(url),
        userId,
      });
    }

    try {
      const response = await globalThis.fetch(input, init);
      const durationMs = Date.now() - startTime;

      options?.onResponse?.(url, method, response.status, durationMs);

      if (useInfraViz) {
        sendTrace(infraVizUrl, {
          id: traceId,
          type: 'response',
          timestamp: Date.now(),
          source: infraViz.source,
          target: extractTarget(url),
          method,
          path: extractPath(url),
          statusCode: response.status,
          durationMs,
          userId,
        });
      }

      return response;
    } catch (error) {
      const durationMs = Date.now() - startTime;
      const message = error instanceof Error ? error.message : String(error);

      options?.onError?.(url, method, message, durationMs);

      if (useInfraViz) {
        sendTrace(infraVizUrl, {
          id: traceId,
          type: 'error',
          timestamp: Date.now(),
          source: infraViz.source,
          target: extractTarget(url),
          method,
          path: extractPath(url),
          durationMs,
          error: message,
          userId,
        });
      }

      throw error;
    }
  };
}
